<template>
  <cv-combo-box
    ref="containerEl"
    v-bind="$attrs"
    :options="dataOptions"
    :label="label"
    :model-value="value"
    :auto-filter="autoFilter && userInput"
    :class="cssClasses"
    :disabled="disabled"
    @change="onChange"
  >
    <template v-for="slot in Object.keys(slots)" #[slot]="slotData">
      <slot :name="slot" v-bind="slotData || {}" />
    </template>
  </cv-combo-box>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useAttrs, useSlots, useTemplateRef, watch } from "vue";
import DOMUtils from "@utils/dom.utils";
import { ComboBoxDropdownOption, CvComboBoxComponent } from "@plugins/common/types.ts";
import { isEmpty } from "@utils/var.check";
import DropDownMixin from "../mixin/dropdown.mixin";
import { useVm } from "@utils/vm.ts";

// Props
interface Props {
  autoFilter?: boolean;
  label?: string;
  value?: string;
  hideClearButton?: boolean;
  disabled?: boolean;
  kind?: string; // Named as "kind" to follow Carbon naming convention for bx-tag colors
  options: ComboBoxDropdownOption[];
}

const {
  autoFilter = false,
  label = "Choose an option",
  value = "",
  hideClearButton = false,
  disabled = false,
  kind = "gray",
  options,
} = defineProps<Props>();

// Mixin
defineOptions({
  mixins: [DropDownMixin],
});

// Composables
const { vm } = useVm();
const attrs = useAttrs();
const slots = useSlots();

// Reactive data
const containerEl = useTemplateRef<CvComboBoxComponent>("containerEl");
const dataOptions = ref<ComboBoxDropdownOption[]>(options);
const userInput = ref(false);

// Computed properties
const cssClasses = computed(() => {
  const classObject = {
    [`combo-box-${kind}`]: true,
    "hide-clear-button": hideClearButton,
  };
  return attrs.class
    ? {
        ...attrs.class,
        ...classObject,
      }
    : classObject;
});

// Methods
const onChange = () => {
  userInput.value = false;
  dataOptions.value = options;
  nextTick(() => {
    const input = containerEl.value?.$refs.input;
    DOMUtils.setAttr(input, "title", input?.value);
  });
};

const handleInputChange = (event: Event) => {
  // to filter out unselectable header options
  const target = event.target as HTMLInputElement;
  userInput.value = true;

  if (target.value !== "") {
    dataOptions.value = options.filter((option) => !option.header);

    nextTick(() => {
      vm.processContainerElMenuItems();
    });
  } else {
    dataOptions.value = options;
  }
};

// Watchers
watch(
  () => options,
  () => {
    dataOptions.value = options;
  },
);

watch(
  () => value,
  () => {
    if (isEmpty(value) && disabled) {
      // handle case where combobox is disabled by other user input in another field, selection will not change if value is not one of the options
      const clearFilterButton = containerEl.value?.$el?.querySelector<HTMLButtonElement>(
        ".bx--list-box__selection",
      );
      clearFilterButton?.click();
    }
  },
);

// Lifecycle hooks
onMounted(() => {
  vm.containerElMenuItemSelector = "bx--list-box__menu-item";
  containerEl.value?.$refs.input.addEventListener("input", handleInputChange);
});

defineExpose({ containerEl });
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.cv-combo-box {
  &.combo-box-gray {
    .bx--list-box,
    .bx--text-input {
      background-color: $ui-background;

      &:hover {
        background-color: $hover-ui;
      }
    }
  }

  &.combo-box-white {
    &:hover {
      .bx--list-box,
      .bx--text-input {
        background-color: $hover-ui;
      }
    }

    .bx--list-box,
    .bx--text-input {
      background-color: $ui-01;
    }
  }

  .bx--combo-box {
    border-radius: 10px;
    border-bottom: none;
    background-color: inherit;
    border: 1px solid $ui-03;

    &.bx--list-box--disabled {
      background-color: $disabled-01;
      cursor: not-allowed;

      &:hover {
        background-color: $disabled-01;
      }

      .bx--text-input {
        background-color: $disabled-01;
        pointer-events: none;
        opacity: 0.5;

        &::placeholder {
          color: $disabled-02;
        }
      }

      .bx--list-box__menu-icon {
        opacity: 0.5;

        &::before {
          border-color: $ui-05;
        }
      }
    }

    &.bx--list-box--expanded {
      &:hover {
        background-color: $hover-ui;
      }
    }

    &:focus {
      outline: none;
      border-color: $focus;
    }

    &[data-invalid] {
      .bx--list-box__field {
        .bx--text-input {
          outline: none;
        }
      }

      ~ .bx--form-requirement {
        display: flex;
      }
    }

    > .bx--list-box__field {
      border: 1px solid transparent;
      border-radius: 10px;

      .bx--text-input {
        outline: transparent;
        border: none;
        padding-right: 5rem;
      }

      &:focus {
        outline: none;
      }
    }

    .bx--list-box__menu {
      border-radius: 10px;
      box-shadow: 0 2px 6px #0003;
      cursor: default;

      > .bx--list-box__menu-item {
        @include carbon--type-style("body-short-01", true);

        &.box__menu-header {
          pointer-events: none;

          .bx--list-box__menu-item__option {
            pointer-events: none;
            @include carbon--type-style("label-03", true);
            color: $text-03;
            margin-left: $spacing-04;
            text-transform: uppercase;
          }
        }
      }
    }

    .bx--list-box__menu-icon {
      justify-content: center;
      right: 0;
      width: 2.5rem;
      padding: $spacing-03;
      border-left: 1px solid $ui-02;

      > svg {
        width: 24px;
        height: 24px;
      }
    }

    .bx--list-box__menu-icon--open {
      border-left: none;
      border-right: 1px solid $ui-02;
    }

    .bx--list-box__selection {
      right: 3rem;

      svg {
        background-color: $icon-02;
        border-radius: 100%;
        fill: $icon-03;
      }
    }

    .bx--list-box__menu-item__option {
      padding-left: calc(#{$spacing-05} * var(--depth, 0));
    }

    // Hide bottom border for text-input inside a combo box
    .bx--text-input {
      border: none;

      &::placeholder {
        color: $text-01;
        white-space: nowrap;
      }

      &:placeholder-shown {
        text-overflow: ellipsis;
      }
    }
  }

  &.hide-clear-button {
    .bx--list-box__selection {
      display: none;
    }
  }
}
</style>
